import { UserAction, NestedHome, NestedProject } from '@habity/types';
import { UseToastOptions, useToast as useChakraToast } from '@chakra-ui/react';
import { ReactElement, useRef } from 'react';

/**
 * Centralized toast messages for user actions
 * This utility provides consistent toast messages across the application
 */

export interface ToastMessage {
  title: string;
  description: string;
}

/**
 * Toast style options with icon support
 */
export interface ToastStyleOptions {
  icon?: ReactElement;
  duration?: number;
  variant?: string;
  status?: 'success' | 'error' | 'warning' | 'info';
}

/**
 * Get consistent toast options with optional icon
 */
export const getToastOptions = (
  message: ToastMessage,
  options: ToastStyleOptions = {}
): UseToastOptions => {
  return {
    title: message.title,
    description: message.description,
    status: options.status || 'success',
    variant: options.variant || 'graceful',
    icon: options.icon,
    duration: options.duration || 8000,
    isClosable: true,
    styleConfig: {
      zIndex: 1,
      backgroundColor: 'rgba(0,0,0,0.6)',
    },
  };
}

/**
 * Custom hook for showing toast messages with consistent styling
 * @returns function to show toast with consistent styling
 */
export const useToast = () => {
  const toast = useChakraToast();
  // Use a ref to track active toasts and prevent duplicates
  const activeToastsRef = useRef<Record<string, boolean>>({});
  
  /**
   * Show a toast message with consistent styling
   * @param message Toast message object or string title
   * @param options Toast style options
   */
  const showToast = (
    message: ToastMessage | string,
    options: ToastStyleOptions = {}
  ) => {
    // If message is a string, convert it to a ToastMessage object
    const toastMessage = typeof message === 'string' 
      ? { title: message, description: '' } 
      : message;
    
    // Create a normalized deduplication key based on toast content
    // Normalize by trimming whitespace, converting to lowercase, and removing special characters
    const normalizeText = (text: string) => {
      return text
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
        .replace(/[^\w\s]/g, ''); // Remove special characters
    };
    
    const key = `${normalizeText(toastMessage.title)}:${normalizeText(toastMessage.description)}`;
    
    // Check if this toast is already active
    if (activeToastsRef.current[key]) {
      return null; // Skip showing duplicate toast
    }
    
    // Get toast options with consistent styling
    const toastOptions = getToastOptions(toastMessage, options);
    
    // Mark this toast as active
    activeToastsRef.current[key] = true;
    
    // Add onCloseComplete to clean up the reference when toast is closed
    const originalOnCloseComplete = toastOptions.onCloseComplete;
    toastOptions.onCloseComplete = () => {
      // Remove from active toasts
      delete activeToastsRef.current[key];
      // Call original onCloseComplete if it exists
      if (originalOnCloseComplete) {
        originalOnCloseComplete();
      }
    };
    
    // Show the toast
    return toast(toastOptions);
  };
  
  return { showToast, toast };
};

/**
 * Get toast message for wishlist actions
 */
export const getWishlistToastMessage = (home?: NestedHome | null): ToastMessage => {
  const street = home?.address?.street || '';
  
  return {
    title: street ? `${street} har sparats` : 'Bostad sparad',
    description: 'Du hittar bostaden under Favoriter i menyn.',
  };
};

/**
 * Get toast message for interest actions
 */
export const getInterestToastMessage = (home?: NestedHome | null): ToastMessage => {
  const street = home?.address?.street || '';
  
  return {
    title: street ? `Du har anmält intresse för ${street}` : 'Intresse anmält',
    description: 'Vi har skickat en bekräftelse till din e-post.',
  };
};

/**
 * Get toast message for bidding actions
 */
export const getBiddingToastMessage = (home?: NestedHome | null): ToastMessage => {
  const street = home?.address?.street || '';
  
  return {
    title: street ? `Du följer nu budgivningen av ${street}` : 'Följer budgivning',
    description: 'Vi har skickat en bekräftelse till din e-post.',
  };
};

/**
 * Get toast message for viewing actions
 */
export const getViewingToastMessage = (home?: NestedHome | null, isUnregistered = false): ToastMessage => {
  if (isUnregistered) {
    const street = home?.address?.street || '';
    return {
      title: `Avbokad från visning`,
      description: street ? `Du är avbokad från visningen av ${street}.` : 'Du är avbokad från visningen.',
    };
  }

  const street = home?.address?.street || '';

  return {
    title: street ? `Bokad på visning av ${street}` : 'Bokad på visning',
    description: `Vi har skickat en bekräftelse till din e-post.`,
  };
};

/**
 * Get toast message for project viewing actions
 */
export const getProjectViewingToastMessage = (project?: NestedProject | null, isUnregistered = false): ToastMessage => {
  const projectName = project?.name || project?.heading || '';

  if (isUnregistered) {
    return {
      title: `Avbokad från visning`,
      description: projectName ? `Du är avbokad från visningen av ${projectName}.` : 'Du är avbokad från visningen.',
    };
  }

  return {
    title: projectName ? `Bokad på visning av ${projectName}` : 'Bokad på visning',
    description: `Vi har skickat en bekräftelse till din e-post.`,
  };
};

/**
 * Get toast message for viewing interest actions
 */
export const getViewingInterestToastMessage = (): ToastMessage => {
  return {
    title: 'Intresse för visning registrerat',
    description: 'Vi meddelar dig när en visning blir tillgänglig',
  };
};

/**
 * Get toast message for project interest actions
 */
export const getProjectInterestToastMessage = (project?: NestedProject | null): ToastMessage => {
  const projectName = project?.name || project?.heading || '';
  
  return {
    title: projectName ? `Du har anmält intresse för ${projectName}` : 'Projektintresse anmält',
    description: 'Vi meddelar dig när mer information blir tillgänglig.',
  };
};

/**
 * Get toast message for removing project interest
 */
export const getProjectInterestRemovalToastMessage = (project?: NestedProject | null): ToastMessage => {
  const projectName = project?.name || project?.heading || '';
  
  return {
    title: 'Intresse avanmält',
    description: projectName 
      ? `Du har avanmält intresse för ${projectName}.`
      : 'Du kommer inte längre att få uppdateringar om detta projekt.',
  };
};

/**
 * Get toast message for any action type
 */
export const getActionToastMessage = (
  action: UserAction,
  actionType: string,
  home?: NestedHome | null
): ToastMessage => {
  // Map of action types to toast message functions
  const messageMap: Record<string, () => ToastMessage> = {
    wishlist: () => getWishlistToastMessage(home),
    showed_interest: () => getInterestToastMessage(home),
    booked_viewing: () => getViewingToastMessage(home),
    viewing_unregistered: () => getViewingToastMessage(home, true),
    booked_viewing_interest: () => getViewingInterestToastMessage(),
    followed_bidding: () => getBiddingToastMessage(home),
  };

  // Return the message for the specified action type, or fall back to the action
  return (
    messageMap[actionType]?.() ||
    messageMap[action]?.() || {
      title: 'Åtgärd slutförd',
      description: 'Din åtgärd har registrerats',
    }
  );
};

/**
 * Get toast options for any action type with appropriate icon
 */
export const getActionToastOptions = (
  action: UserAction,
  actionType: string,
  home?: NestedHome | null,
  options: ToastStyleOptions = {}
): UseToastOptions => {
  const message = getActionToastMessage(action, actionType, home);
  return getToastOptions(message, options);
};